import { api_v1_band_posts_path, api_v1_band_post_path } from "../../routes"
import { mutationRequest } from "../request"
import { DirectUpload } from "@rails/activestorage"
import imageCompression from "browser-image-compression"
import { isVideoFile } from "../videoThumbnail"
import { getBackgroundVideoUpload } from "../backgroundVideoUpload"

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
}

const compressImageFile = async (file) => {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file
  try {
    return await imageCompression(file, IMAGE_COMPRESSION_OPTIONS)
  } catch (e) {
    console.warn("Image compression failed, using original:", e)
    return file
  }
}

const EXTENSION_FILE_TYPES = {
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/png": "png",
  "image/svg+xml": "svg",
  "image/webp": "webp",
}

// Compresses (images only - a no-op for video) then performs the actual
// DirectUpload, reporting real byte-level progress via onProgress. Shared by
// both the early, silent video upload kicked off at attach-time
// (ImagesUpload/index.js) and the bulk upload below.
export const uploadSingleFile = (file, filename, onProgress) => {
  return compressImageFile(file).then((compressedFile) => {
    return new Promise((resolve) => {
      const upload = new DirectUpload(
        // Explicit `type:` here matters more than it used to: the blob's
        // content_type is otherwise blank until ActiveStorage's own
        // AnalyzeJob corrects it from actual file bytes, which is
        // asynchronous and doesn't happen instantly. That was harmless
        // before (Mux conversion only ever ran once the comment/post was
        // created, well after AnalyzeJob had time to run) but now that a
        // video's Mux conversion can be triggered immediately after upload
        // (see ImagesUpload/index.js), a blank content_type would make
        // VideoConverterService silently skip it as "not a video" - only to
        // succeed later when the record-based fallback runs.
        new File([compressedFile], filename.toLowerCase(), { type: compressedFile.type }),
        "/rails/active_storage/direct_uploads",
        {
          // Fired once, right before the actual PUT to storage - this is
          // where real, byte-level upload progress is available (the
          // blob-creation POST beforehand is negligible/instant by
          // comparison).
          directUploadWillStoreFileWithXHR: (xhr) => {
            xhr.upload.addEventListener("progress", (event) => {
              if (!event.lengthComputable || !onProgress) return
              onProgress(Math.round((event.loaded / event.total) * 100))
            })
          },
        },
      )
      upload.create((error, blob) => {
        if (error || !blob) {
          console.error("error:", error)
          resolve({ error: error || new Error("No blob returned") })
        } else {
          resolve({ signedId: blob.signed_id })
        }
      })
    })
  })
}

export const uploadImages = (imagePreviews, setImagePreviews) => {
  let signedIds = []
  let errors = []
  let resolved = 0
  const total = imagePreviews.length
  const uploadPromise = new Promise((resolve) => {
    const maybeResolve = () => {
      if (resolved === total) resolve({ signedIds: signedIds, errors: errors })
    }
    const applyOutcome = (index, imagePreview, outcome) => {
      resolved += 1
      if (outcome.error) {
        errors.push(outcome.error)
      } else {
        signedIds[index] = outcome.signedId
        const newImagePreviewHash = {
          ...imagePreview,
          signedId: outcome.signedId,
          isUploading: false,
          isUploaded: true,
        }
        setImagePreviews((currentImagePreviews) =>
          currentImagePreviews.map((preview, i) => (i === index ? newImagePreviewHash : preview)),
        )
      }
      maybeResolve()
    }

    imagePreviews.forEach((imagePreview, index) => {
      if (imagePreview.signedId) {
        // This image has already been stored - doesn't need re-uploaded
        const newImagePreviewHash = { ...imagePreview, isUploaded: true }
        setImagePreviews((currentImagePreviews) => [
          ...currentImagePreviews.slice(0, index),
          newImagePreviewHash,
          ...currentImagePreviews.slice(index + 1),
        ])
        resolved += 1
        signedIds[index] = imagePreview.signedId
        maybeResolve()
        return
      }

      // Videos start uploading silently as soon as they're attached (see
      // ImagesUpload/index.js) - reuse that same upload here instead of
      // starting a redundant one.
      const backgroundUpload =
        imagePreview.file && isVideoFile(imagePreview.file)
          ? getBackgroundVideoUpload(imagePreview.file)
          : undefined

      if (backgroundUpload?.result) {
        // Already finished before Post Comment was clicked - go straight to
        // comment creation, no upload UI shown at all.
        applyOutcome(index, imagePreview, backgroundUpload.result)
        return
      }

      if (backgroundUpload) {
        // Still in flight - only now reveal the progress UI, picking up
        // from whatever byte-progress already silently accumulated.
        setImagePreviews((currentImagePreviews) =>
          currentImagePreviews.map((preview, i) =>
            i === index ? { ...preview, isUploading: true } : preview,
          ),
        )
        backgroundUpload.promise.then((outcome) => applyOutcome(index, imagePreview, outcome))
        return
      }

      // Images - uploaded fresh at submit time, same as always.
      const newImagePreviewHash = { ...imagePreview, isUploading: true }
      setImagePreviews((currentImagePreviews) => [
        ...currentImagePreviews.slice(0, index),
        newImagePreviewHash,
        ...currentImagePreviews.slice(index + 1),
      ])
      uploadSingleFile(imagePreview.file, imagePreview.filename, (percent) => {
        setImagePreviews((currentImagePreviews) =>
          currentImagePreviews.map((preview, i) =>
            i === index ? { ...preview, uploadProgress: percent } : preview,
          ),
        )
      }).then((outcome) => applyOutcome(index, imagePreview, outcome))
    })
  })
  return uploadPromise
}

export const updateHeadlinePost = async (
  bandId,
  postId,
  postType,
  contentHash,
  relatedPostId,
  signedIds,
) => {
  try {
    const postPayload = {
      post: {
        post_type: postType,
        title: contentHash.headline_text,
        content_hash: contentHash,
        related_post_id: relatedPostId,
        attached_media: signedIds,
      },
    }
    let responseData = await mutationRequest({
      path: api_v1_band_post_path(bandId, postId),
      options: { method: "PATCH" },
      data: postPayload,
    })
    return responseData
  } catch (error) {
    return { error }
  }
}

export const createHeadlinePost = async (
  bandId,
  postType,
  contentHash,
  relatedPostId,
  signedIds,
) => {
  try {
    const postPayload = {
      post: {
        post_type: postType,
        title: contentHash.headline_text,
        content_hash: contentHash,
        related_post_id: relatedPostId,
        attached_media: signedIds,
      },
    }
    let responseData = await mutationRequest({
      path: api_v1_band_posts_path(bandId),
      options: { method: "POST" },
      data: postPayload,
    })
    return responseData
  } catch (error) {
    return { error }
  }
}

export const destroyPost = async (postId, bandId) => {
  return mutationRequest({
    path: `/api/v1/bands/${bandId}/posts/${postId}`,
    options: { method: "DELETE" },
  })
}

export const cancelEventPost = async (postId, bandId, options = {}) => {
  const { canceled_event_message, notify_attendees } = options
  return mutationRequest({
    path: `/api/v1/bands/${bandId}/posts/${postId}/cancel`,
    options: { method: "POST" },
    data: {
      canceled_event_message: canceled_event_message || null,
      notify_attendees: !!notify_attendees,
    },
  })
}

export const createEvent = async (
  bandId,
  postHash,
  userTags,
  sendNotifications = false,
  copyCoverFromPostId = null,
) => {
  try {
    const data = {
      post: postHash,
      user_tags: userTags,
      send_notifications: sendNotifications,
    }
    if (copyCoverFromPostId != null) {
      data.copy_cover_from_post_id = copyCoverFromPostId
    }
    return mutationRequest({
      path: `/api/v1/bands/${bandId}/posts`,
      options: { method: "POST" },
      data,
    })
  } catch (error) {
    return { error }
  }
}

export const updateEvent = async (
  bandId,
  postId,
  postHash,
  userTags,
  sendNotifications = false,
) => {
  try {
    return mutationRequest({
      path: `/api/v1/bands/${bandId}/posts/${postId}`,
      options: { method: "PUT" },
      data: {
        post: postHash,
        user_tags: userTags,
        send_notifications: sendNotifications,
      },
    })
  } catch (error) {
    return { error }
  }
}

const getNewFilenameFor = (file, uploadFormat) => {
  const filenamePieces = file.name.toLowerCase().split(".")
  const fileExtension = EXTENSION_FILE_TYPES[file.type] || filenamePieces[-1]
  return `${filenamePieces[0]}_${uploadFormat}.${fileExtension}`
}

const fileInfoFor = (file) =>
  file ? {name: file.name, size: file.size, type: file.type} : null

export const uploadEventImages = async (originalFile, croppedBlob) => {
  let croppedSignedId = null
  let originalSignedId = null
  const compressedOriginal = await compressImageFile(originalFile)
  const uploadPromise = new Promise((resolve) => {
    const originalFilename = getNewFilenameFor(originalFile, "original")
    const originalUpload = new DirectUpload(
      new File([compressedOriginal], originalFilename),
      "/rails/active_storage/direct_uploads",
    )
    originalUpload.create((error, blob) => {
      if (error) {
        console.error("error:", error)
        resolve({
          error:
            "Sorry there was an error uploading your original image. Please try again or email support@bandnada.com if it keeps happening.",
          uploadStage: "original",
          // @rails/activestorage's error strings encode exactly where things
          // failed - "Error reading X" (local FileReader failure),
          // "Error creating Blob for X. Status: N" (POST to
          // /rails/active_storage/direct_uploads failed), or
          // "Error storing X. Status: N" (the direct PUT to S3 failed) - so
          // this alone tells us which of the three stages broke.
          rawError: String(error),
          fileInfo: fileInfoFor(originalFile),
        })
      } else {
        originalSignedId = blob.signed_id
        if (originalSignedId && (croppedSignedId || !croppedBlob)) {
          resolve({
            croppedSignedId: croppedSignedId,
            originalSignedId: originalSignedId,
          })
        }
      }
    })

    const croppedFilename = getNewFilenameFor(originalFile, "16x9")
    const croppedUpload = new DirectUpload(
      new File([croppedBlob], croppedFilename),
      "/rails/active_storage/direct_uploads",
    )
    croppedUpload.create((error, blob) => {
      if (error) {
        console.error("error:", error)
        resolve({
          error:
            "Sorry there was an error uploading your cropped image. Please try again or email support@bandnada.com if it keeps happening.",
          uploadStage: "cropped",
          rawError: String(error),
          fileInfo: fileInfoFor(croppedBlob),
        })
      } else {
        croppedSignedId = blob.signed_id
      }
      if (originalSignedId && croppedSignedId) {
        resolve({
          croppedSignedId: croppedSignedId,
          originalSignedId: originalSignedId,
        })
      }
    })
  })
  return uploadPromise
}
